import { Component, ElementRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ProductService } from "./product.service";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ToastrService } from "ngx-toastr";
import { finalize } from 'rxjs';
import { AuthService } from 'src/app/shared/services/auth.service';

type TotpAction = 'create' | 'toggle';

@Component({
  selector: 'app-product',
  templateUrl: './product.component.html',
  styleUrls: ['./product.component.scss']
})
export class ProductComponent {
  public products: any[] = [];
  public total: number = 0;

  public page: number = 1;
  public pageSize: number = 20;

  public isInsert = false;
  public isSubmitted = false;
  public isLoaded = false;

  public selectedWallet: any = null;

  public errorMessage: string = '';
  public validationCode: string = '';
  public validatingTotp: boolean = false;

  private pendingAction: TotpAction | null = null;
  private pendingCreatePayload: any = null;

  @ViewChild('requestTOTPCode') requestTOTPCode!: ElementRef;

  product = new FormGroup({
    type: new FormControl<string>('', Validators.required),
    name: new FormControl<string>('', Validators.required),
    message: new FormControl<string>('', Validators.required),
  });

  constructor(
    private productService: ProductService,
    private modalService: NgbModal,
    private toastr: ToastrService,
    private authService: AuthService
  ) {
    this.loadProducts();
  }

  loadProducts() {
    this.isLoaded = false;

    this.productService.getProducts(this.page, this.pageSize)
      .pipe(finalize(() => (this.isLoaded = true)))
      .subscribe({
        next: (res) => {
          this.products = res?.items ?? [];
          this.total = res?.total ?? 0;
        },
        error: (err) => {
          this.products = [];
          this.total = 0;
          this.toastr.error('Erro ao carregar modalidades');
          console.error(err);
        }
      });
  }

  pageChange() {
    this.loadProducts();
  }

  openInsert() {
    this.isInsert = true;
  }

  return() {
    this.isInsert = false;
    this.clearForm();
  }

  clearForm() {
    this.product.reset();
    this.isSubmitted = false;
  }

  private upper(v: unknown): string {
    return (v ?? '').toString().trim().toUpperCase();
  }

  add() {
    this.isSubmitted = true;
    if (!this.product.valid) return;

    const payload = {
      type: this.upper(this.product.controls.type.value),
      name: this.upper(this.product.controls.name.value),
      message: this.upper(this.product.controls.message.value),
      active: true,
    };

    this.pendingAction = 'create';
    this.pendingCreatePayload = payload;
    this.openTotpModal();
  }

  enableDisableWallet() {
    this.productService.enableDisableWallet({
      accountId: this.selectedWallet.accountId,
      id: this.selectedWallet.id,
      isEnable: !this.selectedWallet.active,
    }).subscribe({
      next: () => {
        this.loadProducts();
        this.modalService.dismissAll();
      },
      error: (err) => {
        this.toastr.error('Erro ao alterar status');
        console.error(err);
      }
    });
  }

  showConfirmEnableDisable(modal: any, row: any) {
    this.selectedWallet = row;
    this.modalService.open(modal, { size: 'sm' });
  }


  openTotpModal() {
    this.errorMessage = '';
    this.validationCode = '';

    this.modalService.open(this.requestTOTPCode, {
      backdrop: 'static',
      keyboard: true,
      centered: true,
      size: 'sm',
    });
  }

  public clearTotpState() {
    this.errorMessage = '';
    this.validationCode = '';
    this.validatingTotp = false;
    this.pendingAction = null;
    this.pendingCreatePayload = null;
  }

  async pasteTotpCode(): Promise<void> {
    try {
      this.validationCode = (await navigator.clipboard.readText()).trim();
    } catch (err) {
      console.error('Erro ao colar token:', err);
    }
  }

  async isValidTotp(): Promise<boolean> {
    try {
      const res = await this.authService.validateTotpCode(this.validationCode);
      return !!res?.isValid;
    } catch {
      return false;
    }
  }

  async validateTotp() {
    if (this.validatingTotp) return;

    this.validatingTotp = true;
    this.errorMessage = '';

    const ok = await this.isValidTotp();
    if (!ok) {
      this.errorMessage = 'Código de autenticação inválido!';
      this.validatingTotp = false;
      return;
    }

    this.isLoaded = false;

    if (this.pendingAction === 'create') {
      this.productService.add(this.pendingCreatePayload)
        .pipe(finalize(() => {
          this.isLoaded = true;
          this.validatingTotp = false;
        }))
        .subscribe({
          next: () => {
            this.modalService.dismissAll();
            this.clearTotpState();
            this.return();
            this.page = 1;
            this.loadProducts();
            this.toastr.success('Modalidade criada com sucesso!');
          },
          error: (err) => {
            this.toastr.error('Erro ao criar modalidade');
            console.error(err);
          }
        });
      return;
    }

    if (this.pendingAction === 'toggle') {
      // ✅ AQUI era enableDisableWallet — agora é add() na mesma rota
      this.productService.add(this.pendingCreatePayload)
        .pipe(finalize(() => {
          this.isLoaded = true;
          this.validatingTotp = false;
        }))
        .subscribe({
          next: () => {
            this.modalService.dismissAll();
            this.clearTotpState();
            this.loadProducts();
            this.toastr.success('Status alterado com sucesso!');
          },
          error: (err) => {
            this.toastr.error('Erro ao alterar status');
            console.error(err);
          }
        });
      return;
    }

    this.validatingTotp = false;
  }

  confirmToggleProduct() {
    this.openTotpForToggle();
  }


  openTotpForToggle() {
    const row = this.selectedWallet;
    if (!row) return;

    this.modalService.dismissAll();

    this.pendingAction = 'toggle';

    this.pendingCreatePayload = {
      id: row.id,
      type: row.type,
      name: row.name,
      message: row.message,
      active: !row.active,
    };

    this.openTotpModal();
  }


}
